import { Button } from "@/components/ui/button";
import { LogOut, User } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Outlet, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { managerMenuItems } from "@/config/sidebarMenu";
import userAvatar from "@/assets/user_avatar.png";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import GenericSideBar from "@/components/layout/GenericSideBar";
import useAuthStore from "@/store/authStore";
import { useRestaurant } from "@/query/useRestaurant";
import { useEffect } from "react";

export default function ManagerLayout() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { logout, user } = useAuthStore();
  const { data: restaurant, error, isLoading } = useRestaurant(user?.uid ?? "");

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/auth");
    } catch (error) {
      console.error("Logout failed:", error);
      toast.error(i18n.t("auth.logoutError"));
    }
  };

  // Handle errors and missing restaurant in useEffect to avoid state updates during render
  useEffect(() => {
    if (error) {
      console.error("Restaurant fetch error:", error);
      toast.error(t("profile.fetchError"));
      // Don't auto-logout on error, let the user handle it
    }
  }, [error, t]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Handle case where manager has no restaurant
  if (!restaurant && !isLoading) {
    navigate("/manager/no-restaurant");
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="w-full flex h-screen">
        <GenericSideBar menuItems={managerMenuItems} />
        <div className="flex flex-col flex-1">
          <header className="flex items-center justify-between px-4 py-3 shadow-md bg-backgroundLight text-gray-800 dark:bg-gray-800 dark:text-white">
            <SidebarTrigger />
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-semibold">smrt-menu</h1>
            </div>
            <div className="flex gap-2 items-center">
              {/* <Button variant="ghost" size="sm" onClick={toggleDarkMode}>
                {darkMode ? <Sun size={18} /> : <Moon size={18} />}
              </Button> */}
              {/* <Button variant="ghost" size="sm" onClick={toggleLanguage}>
                🌍 {language}
              </Button> */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="rounded-full p-0 h-8 w-8">
                    <img
                      src={userAvatar}
                      alt={user?.email || "User"}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem
                    onClick={() => navigate("/manager/profile")}
                  >
                    <User size={16} className="mr-2 text-gray-500" />
                    {t("usermenu.profile")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-red-500"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} className="mr-2" />
                    {t("sidebar.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          <main className="flex-1 p-6 overflow-auto bg-gray-50 dark:bg-gray-900 text-primaryText dark:text-gray-200">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
